<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { NIcon } from 'naive-ui';
import { IconClose, IconCollapse, IconExpand } from '@/components/design/icons';
import GaiaIconButton from '../base/GaiaIconButton.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral, gaiaShellPadding } from '../themes/tokens';

const props = defineProps({
  /** Pane label, also the vertical label on the collapsed rail. */
  title: {
    type: String,
    required: true,
  },
  /** Mounts the collapse control and, when collapsed, the rail. */
  collapsible: {
    type: Boolean,
    default: false,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
  /** Item count shown under the rail's vertical label while collapsed. */
  count: {
    type: String,
    default: undefined,
  },
  /** plain = white workspace pane; soft = the tinted report pane. */
  tone: {
    type: String as PropType<'plain' | 'soft'>,
    default: 'plain',
  },
  /**
   * Which edges carry the separating hairline, as a space-separated list —
   * `"top"`, `"right"`, `"left"`, `"top right"`, or `"none"`. A list rather
   * than one edge because a pane sharing a band with a sibling has to rule off
   * both above itself and beside it.
   */
  divider: {
    type: String,
    default: 'none',
  },
  /**
   * Rule the header off from the body. For a pane whose body opens with a
   * heading of its own (the chat rail's thread title) — without it the two
   * headings stack with nothing between them and read as one block. Panes with
   * a toolbar don't need it: the toolbar is the separation.
   */
  headerDivider: {
    type: Boolean,
    default: false,
  },
  /**
   * The pane's own body scrolls. Off for a pane whose content brings its own
   * scroller (the analysis grid, which pins its header row and first column) —
   * two nested scrollers there would strand the sticky header mid-pane.
   */
  scrollBody: {
    type: Boolean,
    default: true,
  },
  /**
   * Scroll the body without drawing its bar — for a narrow list rail, where
   * the bar takes a strip of the rows' width and the rows already show there
   * is more below.
   */
  hideScrollbar: {
    type: Boolean,
    default: false,
  },
  /**
   * Inset the body to the pane's own edges. Off by default because a scrolling
   * list wants its rows to reach the pane's sides (the data room's rows carry
   * their own, narrower gutter); on for a pane holding a document, a grid or a
   * form, which all read against the same edge as the header above them.
   */
  padBody: {
    type: Boolean,
    default: false,
  },
  /**
   * Mounts the expand toggle. Expanding is the caller's to define — over a
   * sibling pane, over the whole column — so the pane only reports the intent.
   */
  expandable: {
    type: Boolean,
    default: false,
  },
  expanded: {
    type: Boolean,
    default: false,
  },
  /** What expanding is called here, e.g. "Expand over the analysis". */
  expandLabel: {
    type: String,
    default: 'Expand',
  },
  /** And what undoing it is called, e.g. "Show the analysis". */
  contractLabel: {
    type: String,
    default: 'Contract',
  },
  /** Mounts the close control. The caller decides what closing does. */
  closable: {
    type: Boolean,
    default: false,
  },
  /** Defaults to "Close <title>". */
  closeLabel: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  'update:collapsed': [collapsed: boolean];
  'update:expanded': [expanded: boolean];
  close: [];
}>();

const isRail = computed(() => props.collapsible && props.collapsed);

const dividerClasses = computed(() =>
  props.divider
    .split(/\s+/)
    .filter(edge => edge && edge !== 'none')
    .map(edge => `divider-${edge}`)
);

const expandTitle = computed(() => (props.expanded ? props.contractLabel : props.expandLabel));
const closeTitle = computed(() => props.closeLabel ?? `Close ${props.title.toLowerCase()}`);

const border = gaiaNeutral.borderMuted;
const borderSubtle = gaiaNeutral.borderSubtle;
const surface = gaiaNeutral.surface;
const surfaceSoft = gaiaNeutral.surfaceSoft;
const railLabel = gaiaNeutral.textTertiary;
const railCount = gaiaNeutral.textFaint;
const railCountSize = gaiaFontSize.nano;
const titleSize = gaiaFontSize.row;
const semibold = String(gaiaFontWeight.semibold);
const paneInset = gaiaShellPadding.paneX;
</script>

<template>
  <!-- One pane of the review workspace (Review Projects mock: Data Room,
       Analysis, Report, Chat): a header carrying the label and the pane's own
       controls, an optional toolbar strip, the body, and an optional footer.
       Collapsing turns it into a 52px rail with the label set vertically, which
       is what lets the data room get out of the analysis grid's way.
       Bespoke: naive's Card has no rail state, no footer rule and no
       header/toolbar split. -->
  <!-- One <section> for both states, not a v-if/v-else pair: this component is
       the root of the panes the workspace sizes with a `:style`, and a fragment
       root would silently drop that attribute. -->
  <section
    class="gaia-workspace-pane"
    :class="[dividerClasses, { rail: isRail, soft: props.tone === 'soft' && !isRail }]"
  >
    <template v-if="isRail">
      <gaia-icon-button
        :dimension="28"
        :title="`Expand ${props.title.toLowerCase()}`"
        :aria-label="`Expand ${props.title.toLowerCase()}`"
        @click="emit('update:collapsed', false)"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M6 3.5 10.5 8 6 12.5" />
        </svg>
      </gaia-icon-button>
      <div class="rail-label">{{ props.title }}</div>
      <div v-if="props.count" class="rail-count">{{ props.count }}</div>
    </template>

    <template v-else>
      <header class="pane-header" :class="{ ruled: props.headerDivider }">
        <span v-if="$slots.icon" class="pane-icon"><slot name="icon" /></span>
        <!-- #title replaces the label with a control of the caller's own — the
           data room's view switcher opens its menu from this spot. -->
        <slot name="title">
          <gaia-text
            truncate
            tag="div"
            :style="{ fontSize: titleSize, fontWeight: semibold, minWidth: 0 }"
            tone="ink"
          >
            {{ props.title }}
          </gaia-text>
        </slot>
        <n-flex
          v-if="$slots.actions || props.expandable || props.closable || props.collapsible"
          align="center"
          :size="8"
          :wrap="false"
          class="pane-actions"
        >
          <slot name="actions" />
          <!-- The pane's own controls close the cluster, after whatever the
               caller put in #actions: they act on the pane rather than on what
               is in it, so they read as the end of the row, not the start. -->
          <gaia-icon-button
            v-if="props.expandable"
            :dimension="26"
            :title="expandTitle"
            :aria-label="expandTitle"
            @click="emit('update:expanded', !props.expanded)"
          >
            <n-icon :component="props.expanded ? IconCollapse : IconExpand" :size="14" />
          </gaia-icon-button>
          <gaia-icon-button
            v-if="props.closable"
            :dimension="26"
            :title="closeTitle"
            :aria-label="closeTitle"
            @click="emit('close')"
          >
            <n-icon :component="IconClose" :size="15" />
          </gaia-icon-button>
          <!-- 26px is the themed tiny height, so it squares off against its
               neighbour. -->
          <gaia-icon-button
            v-if="props.collapsible"
            size="tiny"
            :dimension="26"
            :title="`Collapse ${props.title.toLowerCase()}`"
            :aria-label="`Collapse ${props.title.toLowerCase()}`"
            @click="emit('update:collapsed', true)"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M10 3.5 5.5 8 10 12.5" />
            </svg>
          </gaia-icon-button>
        </n-flex>
      </header>

      <div v-if="$slots.toolbar" class="pane-toolbar"><slot name="toolbar" /></div>

      <div
        class="pane-body"
        :class="{
          scroll: props.scrollBody,
          padded: props.padBody,
          'no-scrollbar': props.hideScrollbar,
        }"
      >
        <slot />
      </div>

      <footer v-if="$slots.footer" class="pane-footer"><slot name="footer" /></footer>
    </template>
  </section>
</template>

<style scoped>
/* Fills the track it is mounted in, whichever way that track runs —
   WorkspaceLayout's rail and pane column are flex columns and its stacked band
   is a flex row, and in all three `flex-basis: auto` would size the pane to its
   content and leave the rest of the track blank. A caller that needs a specific
   share sets its own inline `flex` (the analysis grid's 55%).

   The custom properties are the pane's geometry published to whatever is
   slotted into it: a list that renders its own strip inside the body (the data
   room's search row and pager, which the pane cannot reach through the slot
   boundary) lines up with the pane's header by reading these rather than by
   restating the numbers and drifting the next time they change. */
.gaia-workspace-pane {
  --gaia-pane-inset: v-bind(paneInset);
  --gaia-pane-toolbar-padding: 11px var(--gaia-pane-inset) 10px;
  --gaia-pane-footer-padding: 10px var(--gaia-pane-inset);
  --gaia-pane-body-padding: 0 var(--gaia-pane-inset) var(--gaia-pane-inset);
  --gaia-pane-rule: 1px solid v-bind(borderSubtle);

  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: v-bind(surface);
}

.gaia-workspace-pane.soft {
  background: v-bind(surfaceSoft);
}

.gaia-workspace-pane.divider-right {
  border-right: 1px solid v-bind(border);
}

.gaia-workspace-pane.divider-left {
  border-left: 1px solid v-bind(border);
}

.gaia-workspace-pane.divider-top {
  border-top: 1px solid v-bind(border);
}

/* The collapsed rail: the expand control on top, then the label turned on its
   side so a 52px column still says what it holds. */
.gaia-workspace-pane.rail {
  align-items: center;
  gap: 14px;
  padding: 13px 0;
}

.rail-label {
  writing-mode: vertical-rl;
  white-space: nowrap;
  font-size: v-bind(titleSize);
  font-weight: v-bind(semibold);
  color: v-bind(railLabel);
}

.rail-count {
  writing-mode: vertical-rl;
  font-size: v-bind(railCountSize);
  font-variant-numeric: tabular-nums;
  color: v-bind(railCount);
}

.pane-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 13px var(--gaia-pane-inset) 0;
}

/* The rule as a pseudo-element rather than a border, so it starts and ends on
   the pane's inset — on the same x as the header's own text, and as any rule
   the body draws under its first row. A border would span the pane edge to
   edge, and two rules of visibly different widths, one above the other, read
   as a mistake. */
.pane-header.ruled {
  position: relative;
  padding-bottom: 13px;
}

.pane-header.ruled::after {
  content: '';
  position: absolute;
  right: var(--gaia-pane-inset);
  bottom: 0;
  left: var(--gaia-pane-inset);
  height: 1px;
  background: v-bind(borderSubtle);
}

.pane-icon {
  flex: none;
  display: flex;
  align-items: center;
  color: v-bind(railCount);
}

.pane-actions {
  margin-left: auto;
  flex: none;
}

.pane-toolbar {
  flex: none;
  padding: var(--gaia-pane-toolbar-padding);
}

.pane-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Block, not flex: a scrolling body wants its content to overflow it, and a
   flex column shrinks its children towards the container's height first — which
   is how a long list ends up squeezed instead of scrolled. The flex column is
   for the other case, where a single child has to fill the pane exactly. */
.pane-body.scroll {
  display: block;
  overflow: auto;
}

.pane-body.no-scrollbar {
  scrollbar-width: none;
}

.pane-body.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* On the scrolling body this pads the scroll container itself, so the bar stays
   on the pane's edge while the content clears it. */
.pane-body.padded {
  padding: var(--gaia-pane-body-padding);
}

/* Rules off the pane's own summary line (file count, bulk actions, pager). */
.pane-footer {
  flex: none;
  padding: var(--gaia-pane-footer-padding);
  border-top: var(--gaia-pane-rule);
}
</style>
